import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { AES } from "crypto-js";
import Calendar from "../../../helpers/dateComponents/Calendar";
import DownloadImage from "../../../images/icons/download-icon.svg";
import PrintIcon from "../../../images/singleinvoice/print.svg";
import CalendarIcon from "../../../images/icons/calendar-icon.svg";
import TimeIcon from "../../../images/icons/clock.svg";
import DollarIcon from "../../../images/icons/billable.svg";
import NonBillableIcon from "../../../images/icons/non-billable.svg";
import BudgetIcon from "../../../images/icons/purses-money.svg";
import CostIcon from "../../../images/icons/notepad.svg";
import RoiIcon from "../../../images/icons/dollar-reverse.svg";
import {
  GetRoi,
  TotalBudget,
  TotalBudgetedHours,
  TotalCost,
  TrackedProjectHours,
} from "../../../helpers/ProjectHelpers.jsx";
import "./MvpClientProjects.css";

function formatDuration(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  return [hours, minutes, secs].map((part) => String(part).padStart(2, "0")).join(":");
}

function formatMoney(value, currency) {
  const amount = Number(value) || 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
  }).format(amount);
}

function roiLabel(budget, cost) {
  const budgetValue = Number(budget) || 0;
  const costValue = Number(cost) || 0;
  if (costValue === 0 && budgetValue === 0) return "0%";
  if (costValue === 0) return "N/A";
  const value = ((budgetValue - costValue) / costValue) * 100;
  return Number.isNaN(value) ? "0%" : `${value.toFixed(0)}%`;
}

function pageItems(current, total) {
  if (total <= 5) return Array.from({ length: total }, (_, index) => index + 1);
  const items = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) items.push("ellipsis-start");
  for (let page = start; page <= end; page += 1) items.push(page);
  if (end < total - 1) items.push("ellipsis-end");
  items.push(total);
  return items;
}

function HoverText({ text }) {
  const clipRef = useRef(null);
  const [truncated, setTruncated] = useState(false);
  const value = text || "—";

  useEffect(() => {
    const el = clipRef.current;
    if (!el) return undefined;
    const measure = () => setTruncated(el.scrollWidth > el.clientWidth + 1);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [value]);

  return (
    <span className="mvp-proj-hover">
      <span ref={clipRef} className="mvp-proj-hover-clip">{value}</span>
      {truncated && <span className="mvp-proj-hover-full">{value}</span>}
    </span>
  );
}

function ClientMark({ client }) {
  const logo = client?.company_logo && client.company_logo !== "null" ? client.company_logo : "";
  const [failed, setFailed] = useState(false);
  if (!logo || failed) {
    return <span className="mvp-proj-mark" aria-hidden="true" />;
  }
  return (
    <img
      className="mvp-proj-logo"
      src={`/uploads/clientCompyLogo/lowQuality/${logo.replace(/[^a-zA-Z0-9._-]/g, "")}`}
      alt=""
      onError={() => setFailed(true)}
    />
  );
}

function FilterSelect({ label, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div className="mvp-proj-filter" ref={ref}>
      <span className="mvp-proj-label">{label}</span>
      <button type="button" className="mvp-proj-trigger" onClick={() => setOpen((current) => !current)}>
        <span>{value}</span>
        <svg className="mvp-proj-arrow" xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none" aria-hidden="true">
          <path d="M0.404297 0.444336L5.9043 5.44434L11.4043 0.444336" strokeWidth="1.2" stroke="#405BFF" />
        </svg>
      </button>
      {open && (
        <ul className="mvp-proj-menu">
          {options.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                className={option.label === value ? "is-active" : ""}
                onClick={() => {
                  onChange(option.id);
                  setOpen(false);
                }}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function MvpClientProjects({
  clients,
  projects,
  selectAllClient,
  selectedClients,
  selectAllProject,
  selectedProjects,
  onSelectClient,
  onSelectProject,
  selectedStatus,
  onSelectStatus,
  dateRange,
  calendarRef,
  showCalendar,
  onToggleCalendar,
  onCancelCalendar,
  onDateChange,
  total,
  rowsPerPage,
  currentPage,
  totalPages,
  onPageChange,
  rows,
  isLoading,
  onNewProject,
  onExport,
  onPrint,
  exportLoading,
  printLoading,
  currency,
  totalDuration,
  totalBillable,
  totalNonBillable,
  totalBudget,
  totalCost,
  statsLoading,
}) {
  const tableRef = useRef(null);
  const clientOptions = [
    { id: "all", label: "All" },
    ...clients.map((client) => ({
      id: client._id,
      label: client.client_name || client.legal_entity_name || "Client",
    })),
  ];
  const projectOptions = [
    { id: "all", label: "All" },
    ...projects.map((item) => ({ id: item._id, label: item.project_name || "Project" })),
  ];
  const statusOptions = [
    { id: "all", label: "All" },
    { id: "Active", label: "Active" },
    { id: "Inactive", label: "Inactive" },
    { id: "Archived", label: "Archived" },
  ];

  const clientLabel = selectAllClient || selectedClients.length !== 1
    ? "All"
    : clientOptions.find((option) => option.id === selectedClients[0])?.label || "All";
  const projectLabel = selectAllProject || selectedProjects.length !== 1
    ? "All"
    : projectOptions.find((option) => option.id === selectedProjects[0])?.label || "All";
  const statusLabel = selectedStatus || "All";
  const dateLabel = dateRange?.[0] && dateRange?.[1]
    ? `${moment(dateRange[0]).format("MM/DD/YY")} - ${moment(dateRange[1]).format("MM/DD/YY")}`
    : "mm/dd/yy";

  const cards = [
    ["Total Hours", formatDuration(totalDuration), TimeIcon],
    ["Total Billable", formatDuration(totalBillable), DollarIcon],
    ["Total Non-Billable", formatDuration(totalNonBillable), NonBillableIcon],
    ["Total Budget", formatMoney(totalBudget, currency), BudgetIcon],
    ["Total Cost", formatMoney(totalCost, currency), CostIcon],
    ["ROI", roiLabel(totalBudget, totalCost), RoiIcon],
  ];

  useEffect(() => {
    const tableEl = tableRef.current;
    const contentOuter = document.querySelector(".content-outer.clients");
    if (!tableEl || !contentOuter) return undefined;

    let atEnd = false;
    const updatePadding = () => {
      if (window.innerWidth > 767) {
        atEnd = false;
        contentOuter.classList.remove("mvp-hscroll-end");
        return;
      }
      const maxScroll = Math.max(0, tableEl.scrollWidth - tableEl.clientWidth);
      if (maxScroll <= 2) {
        atEnd = false;
        contentOuter.classList.remove("mvp-hscroll-end");
        return;
      }
      if (!atEnd && tableEl.scrollLeft >= maxScroll - 4) {
        atEnd = true;
        contentOuter.classList.add("mvp-hscroll-end");
      } else if (atEnd && tableEl.scrollLeft < maxScroll - 24) {
        atEnd = false;
        contentOuter.classList.remove("mvp-hscroll-end");
      }
    };

    tableEl.addEventListener("scroll", updatePadding, { passive: true });
    window.addEventListener("resize", updatePadding);
    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updatePadding) : null;
    resizeObserver?.observe(tableEl);
    updatePadding();

    return () => {
      tableEl.removeEventListener("scroll", updatePadding);
      window.removeEventListener("resize", updatePadding);
      resizeObserver?.disconnect();
      contentOuter.classList.remove("mvp-hscroll-end");
    };
  }, [rows, isLoading]);

  return (
    <div className="mvp-proj">
      <h2 className="mvp-mobile-heading">Projects</h2>
      <div className="mvp-proj-toolbar">
        <div className="mvp-proj-actions">
          <button type="button" className="mvp-proj-btn mvp-proj-btn-primary" onClick={onNewProject}>
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
              <path d="M7 1.5v11M1.5 7h11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            New Project
          </button>
          <button type="button" className="mvp-proj-btn" onClick={onExport} disabled={exportLoading}>
            <span className="mvp-proj-site-icon" style={{ "--icon": `url(${DownloadImage})` }} aria-hidden="true" />
            {exportLoading ? "Exporting…" : "Export"}
          </button>
          <button type="button" className="mvp-proj-btn" onClick={onPrint} disabled={printLoading}>
            <span className="mvp-proj-site-icon mvp-proj-print-icon" style={{ "--icon": `url(${PrintIcon})` }} aria-hidden="true" />
            {printLoading ? "Printing…" : "Print"}
          </button>
        </div>
        <div className="mvp-proj-count">
          <span>Showing</span>
          <span className="mvp-proj-count-box">{rowsPerPage}</span>
          <span>of {total} results</span>
        </div>
      </div>

      <div className="mvp-proj-filters">
        <FilterSelect label="Client" value={clientLabel} options={clientOptions} onChange={onSelectClient} />
        <FilterSelect label="Project" value={projectLabel} options={projectOptions} onChange={onSelectProject} />
        <div className="mvp-proj-filter" ref={calendarRef}>
          <span className="mvp-proj-label">Date Range</span>
          <button type="button" className="mvp-proj-trigger mvp-proj-date" onClick={onToggleCalendar}>
            <span>{dateLabel}</span>
            <img src={CalendarIcon} alt="" />
          </button>
          {showCalendar && (
            <div className="mvp-proj-calendar date-range-new left-align">
              <Calendar
                onCancel={onCancelCalendar}
                onChange={onDateChange}
                initialRange={{ startDate: dateRange ? dateRange[0] : null, endDate: dateRange ? dateRange[1] : null }}
              />
            </div>
          )}
        </div>
        <FilterSelect
          label="Status"
          value={statusLabel}
          options={statusOptions}
          onChange={(id) => onSelectStatus(id === "all" ? null : id)}
        />
      </div>

      <div className="mvp-proj-cards">
        {cards.map(([label, value, icon]) => (
          <div className="mvp-proj-card" key={label}>
            <div className="mvp-proj-card-top">
              <span>{label}</span>
              <img src={icon} alt="" />
            </div>
            {statsLoading ? <span className="mvp-proj-skel mvp-proj-skel-value" /> : <strong>{value}</strong>}
          </div>
        ))}
      </div>

      <div className="mvp-proj-scroll" ref={tableRef}>
        <table>
          <thead>
            <tr>
              <th className="is-sticky sticky-project">Project</th>
              <th className="is-sticky sticky-client">Client</th>
              <th className="is-sticky sticky-status">Status</th>
              <th className="is-sticky sticky-details" aria-label="Details" />
              <th>ROI</th>
              <th>Budget</th>
              <th>Cost</th>
              <th>Budgeted Hours</th>
              <th>Tracked Hours</th>
              <th>Date Created</th>
              <th>Billable</th>
            </tr>
          </thead>
          <tbody>
            {isLoading &&
              Array.from({ length: Math.max(rowsPerPage || 6, 6) }).map((_, index) => (
                <tr key={`project-skeleton-${index}`} className="mvp-proj-skel-row">
                  <td className="is-sticky sticky-project"><span className="mvp-proj-skel mvp-proj-skel-name" /></td>
                  <td className="is-sticky sticky-client">
                    <span className="mvp-proj-skel-client">
                      <span className="mvp-proj-skel mvp-proj-skel-mark" />
                      <span className="mvp-proj-skel mvp-proj-skel-text" />
                    </span>
                  </td>
                  <td className="is-sticky sticky-status"><span className="mvp-proj-skel mvp-proj-skel-pill" /></td>
                  <td className="is-sticky sticky-details"><span className="mvp-proj-skel mvp-proj-skel-btn" /></td>
                  <td><span className="mvp-proj-skel mvp-proj-skel-short" /></td>
                  <td><span className="mvp-proj-skel mvp-proj-skel-money" /></td>
                  <td><span className="mvp-proj-skel mvp-proj-skel-money" /></td>
                  <td><span className="mvp-proj-skel mvp-proj-skel-time" /></td>
                  <td><span className="mvp-proj-skel mvp-proj-skel-time" /></td>
                  <td><span className="mvp-proj-skel mvp-proj-skel-date" /></td>
                  <td><span className="mvp-proj-skel mvp-proj-skel-pill" /></td>
                </tr>
              ))}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td className="mvp-proj-empty" colSpan={11}>No projects match this view.</td>
              </tr>
            )}
            {!isLoading && rows.map((row) => {
              const client = row.client_name;
              const clientName = client?.client_name || client?.legal_entity_name || "—";
              const encryptedCliId = client?._id ? AES.encrypt(client._id, "encryptionKey").toString() : "";
              const encodedId = encodeURIComponent(encryptedCliId).replace(/\//g, "%2F");
              const statusName = row.is_archived ? "Archived" : row.is_active ? "Active" : "Inactive";
              return (
                <tr key={row._id}>
                  <td className="is-sticky sticky-project">
                    <div className="mvp-proj-name">
                      <HoverText text={row.project_name} />
                      {client?._id ? (
                        <Link className="mvp-row-chevron" to={`/client/projects/details/${row._id}/${encodedId}`} aria-label={`Open ${row.project_name}`}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="11" height="19" viewBox="0 0 11 19" fill="none" aria-hidden="true">
                            <path d="M0.423828 18.4238L9.42383 9.42383L0.423827 0.423828" strokeWidth="1.2" stroke="#405BFF" />
                          </svg>
                        </Link>
                      ) : null}
                    </div>
                  </td>
                  <td className="is-sticky sticky-client">
                    <div className="mvp-proj-client">
                      <ClientMark client={client} />
                      <HoverText text={clientName} />
                    </div>
                  </td>
                  <td className="is-sticky sticky-status">
                    <span className={`mvp-proj-pill ${statusName === "Active" ? "is-active" : "is-muted"}`}>{statusName}</span>
                  </td>
                  <td className="is-sticky sticky-details">
                    {client?._id ? (
                      <Link className="mvp-proj-details" to={`/client/projects/details/${row._id}/${encodedId}`}>Details</Link>
                    ) : (
                      <span className="mvp-proj-details is-disabled">Details</span>
                    )}
                  </td>
                  <td><GetRoi data={row} /></td>
                  <td><TotalBudget data={row} /></td>
                  <td><TotalCost data={row} timers={row.timers} /></td>
                  <td><TotalBudgetedHours data={row} /></td>
                  <td><TrackedProjectHours timers={row.timers} /></td>
                  <td>{row.created_at ? moment(row.created_at).format("MMMM DD, YYYY") : "—"}</td>
                  <td>
                    <span className={row.is_billable ? "mvp-proj-bill is-yes" : "mvp-proj-bill is-no"}>
                      {row.is_billable ? "Yes" : "No"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div className="mvp-proj-pagination">
          <button type="button" className="mvp-proj-page-nav" disabled={currentPage <= 1} onClick={() => onPageChange(currentPage - 1)}>
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <path d="M7.8 2.4 4.2 6l3.6 3.6" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
            Back
          </button>
          {pageItems(currentPage, totalPages).map((item) => (
            typeof item === "string" ? (
              <span key={item} className="mvp-proj-page">...</span>
            ) : (
              <button
                key={item}
                type="button"
                className={item === currentPage ? "mvp-proj-page is-current" : "mvp-proj-page"}
                onClick={() => onPageChange(item)}
              >
                {item}
              </button>
            )
          ))}
          <button type="button" className="mvp-proj-page-nav" disabled={currentPage >= totalPages} onClick={() => onPageChange(currentPage + 1)}>
            Next
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <path d="M4.2 2.4 7.8 6 4.2 9.6" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
